import { useState, type ReactNode } from 'react';
import type { V2Evidence } from '../../domain/evidence';
import { useT } from '../../i18n/i18n';
import type { EvidenceTarget } from '../../state/captureFlow';
import { useMediaUrl } from '../../state/mediaUrls';
import { ActionSheet, Button, InlineMessage, PhotoCard } from '../../ui/components';
import { PhotoPoolPickerSheet } from '../import/PhotoPoolPickerSheet';
import { PhotoNoteSheet } from './PhotoNoteSheet';
import { useEvidenceCapture } from './useEvidenceCapture';

// Photo surface used by areas, items and findings (Authority §8.2 / §8.4; task book §9 / §16):
// 4:3 photo cards with the album outcome printed on every camera photo (never hidden) and — always —
// the note line under each card ("Note: …" / "+ Add Photo Note"); the capture entries 拍照 · 从相册选择 ·
// 照片池 (the inspection's pool, linked without moving or copying).
//   layout   strip = horizontal cards (area scan photos) · grid = two columns (item / finding evidence)
//   actions  pair = the three entries as a row · menu = behind one "+ Add Photo" · none = caller-rendered

function albumLabel(t: ReturnType<typeof useT>['t'], evidence: V2Evidence): string {
  if (evidence.source === 'photoLibrary') return t('photo.imported');
  switch (evidence.albumSaveState) {
    case 'saved':
      return t('photo.albumSaved');
    case 'failed':
      return t('photo.albumFailed');
    case 'unavailable':
      return t('photo.albumUnavailable');
    default:
      return t('photo.albumPending');
  }
}

export function Thumbnail(props: { evidence: V2Evidence; onClick: () => void; selected?: boolean; onEditNote?: (evidence: V2Evidence) => void; onRemove?: (evidence: V2Evidence) => void }) {
  const { t } = useT();
  const url = useMediaUrl(props.evidence.id, 'thumbnail');
  const { evidence } = props;
  return (
    <PhotoCard
      src={url}
      alt={`${t('photo.open')} · ${albumLabel(t, evidence)}`}
      label={albumLabel(t, evidence)}
      flagged={evidence.albumSaveState === 'failed'}
      note={evidence.caption}
      selected={props.selected}
      cornerIcon="open_in_full"
      onOpen={props.onClick}
      onAddNote={props.onEditNote ? () => props.onEditNote?.(evidence) : undefined}
      onEditNote={props.onEditNote ? () => props.onEditNote?.(evidence) : undefined}
      onRemove={props.onRemove ? () => props.onRemove?.(evidence) : undefined}
      mediaTestId="photo-thumb"
      albumState={evidence.albumSaveState}
      testId="photo-card"
    />
  );
}

export interface PoolTarget {
  itemId: string | null;
  findingId: string | null;
  linkedIds: readonly string[];
  onLinked?: (ids: string[]) => void;
}

export function PhotoStrip(props: {
  evidence: V2Evidence[];
  target: EvidenceTarget;
  onOpen: (index: number) => void;
  onCaptured?: (evidence: V2Evidence[]) => void;
  // When given, the photo pool picker is offered as the third entry.
  pool?: PoolTarget;
  emptyText?: string;
  layout?: 'strip' | 'grid';
  actions?: 'pair' | 'menu' | 'none';
  disabled?: boolean;
  // Rendered between the capture actions and the photo cards.
  afterActions?: ReactNode;
  testId?: string;
}) {
  const { t } = useT();
  const capture = useEvidenceCapture(props.target, props.onCaptured);
  const [menuOpen, setMenuOpen] = useState(false);
  const [poolOpen, setPoolOpen] = useState(false);
  const [noteTarget, setNoteTarget] = useState<V2Evidence | null>(null);
  const layout = props.layout ?? 'grid';
  const actions = props.actions ?? 'pair';
  const busy = capture.busy !== null || props.disabled === true;
  const liveNoteTarget = noteTarget ? (props.evidence.find((e) => e.id === noteTarget.id) ?? noteTarget) : null;

  return (
    <div className="photo-strip" data-testid={props.testId ?? 'photo-strip'}>
      {actions === 'pair' ? (
        <div className={props.pool ? 'btn-trio' : 'btn-pair'}>
          <Button variant="tonal" icon="photo_camera" onClick={() => void capture.takePhoto()} disabled={busy} testId="btn-camera">
            {capture.busy === 'camera' ? t('photo.opening') : t('photo.takePhoto')}
          </Button>
          <Button variant="secondary" icon="photo_library" onClick={() => void capture.chooseLibrary()} disabled={busy} testId="btn-library">
            {capture.busy === 'library' ? t('photo.importing') : props.pool ? t('photo.libraryShort') : t('photo.chooseLibrary')}
          </Button>
          {props.pool ? (
            <Button variant="secondary" icon="perm_media" onClick={() => setPoolOpen(true)} disabled={busy} testId="btn-pool">
              {t('pool.title')}
            </Button>
          ) : null}
        </div>
      ) : null}
      {actions === 'menu' ? (
        <Button variant="secondary" icon="add_a_photo" onClick={() => setMenuOpen(true)} disabled={busy} testId="btn-add-photo">
          {capture.busy === 'camera' ? t('photo.opening') : capture.busy === 'library' ? t('photo.importing') : t('photo.addPhoto')}
        </Button>
      ) : null}
      {capture.message ? <InlineMessage kind={capture.message.kind} text={capture.message.text} /> : null}
      {props.afterActions}
      {props.evidence.length === 0 ? (
        <p className="photo-empty">{props.emptyText ?? t('area.photosEmpty')}</p>
      ) : (
        <div className={layout === 'strip' ? 'photo-strip-h' : 'photo-row'}>
          {props.evidence.map((ev, index) => (
            <Thumbnail key={ev.id} evidence={ev} onClick={() => props.onOpen(index)} onEditNote={props.disabled ? undefined : setNoteTarget} />
          ))}
        </div>
      )}
      {menuOpen ? (
        <ActionSheet
          title={t('photo.addPhoto')}
          onClose={() => setMenuOpen(false)}
          layer="over"
          testId="add-photo-sheet"
          items={[
            {
              key: 'camera',
              label: t('photo.takePhoto'),
              icon: 'photo_camera',
              onSelect: () => {
                setMenuOpen(false);
                void capture.takePhoto();
              },
              testId: 'btn-camera'
            },
            {
              key: 'library',
              label: t('photo.chooseLibrary'),
              icon: 'photo_library',
              onSelect: () => {
                setMenuOpen(false);
                void capture.chooseLibrary();
              },
              testId: 'btn-library'
            },
            ...(props.pool
              ? [
                  {
                    key: 'pool',
                    label: t('pool.title'),
                    icon: 'perm_media' as const,
                    onSelect: () => {
                      setMenuOpen(false);
                      setPoolOpen(true);
                    },
                    testId: 'btn-pool'
                  }
                ]
              : [])
          ]}
        />
      ) : null}
      {poolOpen && props.pool ? (
        <PhotoPoolPickerSheet
          inspectionId={props.target.inspectionId}
          itemId={props.pool.itemId}
          findingId={props.pool.findingId}
          linkedIds={props.pool.linkedIds}
          onClose={() => setPoolOpen(false)}
          onLinked={props.pool.onLinked}
        />
      ) : null}
      {liveNoteTarget ? <PhotoNoteSheet evidence={liveNoteTarget} onClose={() => setNoteTarget(null)} /> : null}
    </div>
  );
}
